// Derived character stats and progression curves. Used by the server for the simulation
// and by the client to show the numbers in the character sheet.

import { CLASSES, starterSkills } from './classes';
import { BASE_MOVE_SPEED, BAG_SIZE, ClassId, Derived, HeroSave, Item, MAX_LEVEL, Mods, SLOT_IDS, STASH_SIZE, STAT_POINTS_PER_LEVEL, StatKey } from './types';

/** The first two levels cost less than the curve, so a new hero still levels up soon. */
const EARLY_LEVEL_XP: Record<number, number> = { 1: 150, 2: 400 };

export function xpToNext(level: number): number {
  if (level >= MAX_LEVEL) return Infinity;
  return EARLY_LEVEL_XP[level] ?? 200 + 150 * level + 15 * level * level;
}

export function sumMods(items: (Item | undefined)[], extra?: Mods): Mods {
  const out: Mods = {};
  for (const it of items) {
    if (!it) continue;
    for (const k in it.mods) {
      const key = k as StatKey;
      out[key] = (out[key] ?? 0) + (it.mods[key] ?? 0);
    }
  }
  if (extra) {
    for (const k in extra) {
      const key = k as StatKey;
      out[key] = (out[key] ?? 0) + (extra[key] ?? 0);
    }
  }
  return out;
}

export function computeDerived(hero: HeroSave, buffMods?: Mods): Derived {
  const cls = CLASSES[hero.cls];
  const items = SLOT_IDS.map((s) => hero.equip[s]);
  const m = sumMods(items, buffMods);
  const g = (k: StatKey) => m[k] ?? 0;

  const str = hero.stats.str + g('str');
  const dex = hero.stats.dex + g('dex');
  const vit = hero.stats.vit + g('vit');
  const ene = hero.stats.ene + g('ene');

  const maxHp = Math.round(cls.baseHp + cls.hpPerLevel * hero.level + cls.hpPerVit * vit + g('hp'));
  const maxMp = Math.round(cls.baseMp + cls.mpPerLevel * hero.level + cls.mpPerEne * ene + g('mp'));

  let armor = g('armor');
  for (const it of items) if (it?.armor) armor += it.armor;
  armor = Math.round((armor + dex * 0.15) * (1 + g('armorPct') / 100));

  const weapon = hero.equip.weapon;
  const wMin = (weapon?.dmg?.[0] ?? 2) + g('minDmg');
  const wMax = (weapon?.dmg?.[1] ?? 4) + g('maxDmg');

  const resAll = g('resAll');
  const res = (k: StatKey) => Math.min(75, g(k) + resAll);

  return {
    str, dex, vit, ene,
    maxHp, maxMp,
    hpRegen: 0.4 + vit * 0.015 + g('hpRegen'),
    mpRegen: 1.2 + ene * 0.04 + g('mpRegen'),
    armor,
    minDmg: Math.max(1, wMin),
    maxDmg: Math.max(wMin + 1, wMax),
    dmgPct: g('dmgPct'),
    fireDmg: g('fireDmg'),
    coldDmg: g('coldDmg'),
    lightDmg: g('lightDmg'),
    poisonDmg: g('poisonDmg'),
    atkSpd: g('atkSpd') + dex * 0.05,
    crit: Math.min(60, 4 + dex * 0.06 + g('crit')),
    critDmg: 50 + g('critDmg'),
    moveSpeed: BASE_MOVE_SPEED * (1 + Math.min(80, g('moveSpd')) / 100),
    lifeSteal: Math.min(25, g('lifeSteal')),
    manaSteal: Math.min(25, g('manaSteal')),
    resFire: res('resFire'),
    resCold: res('resCold'),
    resLight: res('resLight'),
    resPoison: res('resPoison'),
    magicFind: g('magicFind'),
    goldFind: g('goldFind'),
    allSkills: g('allSkills'),
    thorns: g('thorns'),
    dmgReduce: Math.min(60, g('dmgReduce')),
    cdr: Math.min(40, g('cdr')),
  };
}

/** Effective rank of a skill including +all skills. Returns 0 if unlearned. */
export function effectiveRank(hero: HeroSave, derived: Derived, skillId: string): number {
  const base = hero.skills[skillId] ?? 0;
  if (base <= 0) return 0;
  return base + Math.floor(derived.allSkills);
}

export function armorReduction(armor: number, attackerLevel: number): number {
  const a = Math.max(0, armor);
  return Math.min(0.75, a / (a + 25 + 8 * attackerLevel));
}

export function newHero(id: string, name: string, cls: ClassId, starterWeapon: Item): HeroSave {
  const def = CLASSES[cls];
  const starters = starterSkills(cls);
  const skills: Record<string, number> = {};
  for (const s of starters) skills[s] = 1;
  const now = Date.now();
  return {
    id,
    name,
    cls,
    level: 1,
    xp: 0,
    stats: { ...def.stats },
    statPts: 0,
    skillPts: 1,
    skills,
    binds: [starters[0], starters[1], null, null, null, null],
    equip: { weapon: starterWeapon },
    bag: new Array(BAG_SIZE).fill(null),
    stash: new Array(STASH_SIZE).fill(null),
    gold: 25,
    hpPots: 5,
    mpPots: 3,
    progress: 0,
    bosses: [],
    deaths: 0,
    kills: 0,
    created: now,
    updated: now,
  };
}

/** Applies accumulated XP to a hero, returning the number of levels gained. */
export function applyLevelUps(hero: HeroSave): number {
  let gained = 0;
  while (hero.level < MAX_LEVEL && hero.xp >= xpToNext(hero.level)) {
    hero.xp -= xpToNext(hero.level);
    hero.level += 1;
    hero.statPts += STAT_POINTS_PER_LEVEL;
    hero.skillPts += 1;
    gained++;
  }
  if (hero.level >= MAX_LEVEL) hero.xp = 0;
  return gained;
}

export function resCap(v: number): number {
  return Math.max(-100, Math.min(90, v));
}

export const STAT_INFO: { key: 'str' | 'dex' | 'vit' | 'ene'; label: string; desc: string }[] = [
  { key: 'str', label: 'Might', desc: 'Melee damage +1% per point' },
  { key: 'dex', label: 'Finesse', desc: 'Ranged damage, crit, attack speed' },
  { key: 'vit', label: 'Vitality', desc: 'Life & life regen' },
  { key: 'ene', label: 'Spirit', desc: 'Spell damage, mana, mana regen' },
];

export const RESPEC_COST = (level: number) => 50 + level * level * 3;

export function cloneHero(h: HeroSave): HeroSave {
  return JSON.parse(JSON.stringify(h));
}
